"use client";

import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "How does revenue management boost hotel profit?",
    answer: "We use data-driven pricing to match room rates with real-time demand. By optimizing your OTA presence and inventory, we ensure you capture the highest possible ADR and occupancy for every single night."
  }, {
    question: "Which hotel types do you support?",
    answer: "We work with independent hotels, boutique properties, and small chains. Our strategies are tailored to your specific market, property size, and guest demographics to ensure maximum revenue impact."
  }, {
    question: "Do you manage OTA channel connections?",
    answer: "Yes. We handle channel manager setup, rate parity, and OTA content optimization. We ensure your property stands out on Booking.com, Expedia, and others to drive direct and indirect bookings."
  }, {
    question: "How long until we see revenue results?",
    answer: "Most clients see improved booking velocity within four weeks. Full revenue optimization, including tech stack integration and long-term pricing strategy, typically delivers peak performance within three months."
  }, {
    question: "How do you track revenue performance?",
    answer: "We track RevPAR, ADR, and occupancy against your historical data and market benchmarks. We provide clear, actionable reports that show exactly how our strategies are impacting your bottom line."
  }, {
    question: "Can you help with hotel tech selection?",
    answer: "Absolutely. We audit your current tech stack and recommend PMS, RMS, and channel management tools that fit your budget and operational needs, ensuring your systems work together seamlessly."
  }, {
    question: "What is your approach to revenue audits?",
    answer: "We perform a deep dive into your pricing history, distribution channels, and competitor set. We identify missed opportunities and provide a roadmap to fix revenue leaks and improve profitability."
  }, {
    question: "How do we start working with HotelRevBaBa?",
    answer: "Reach out via our contact form. We will schedule a discovery call to discuss your property goals, then provide a tailored proposal focused on your specific revenue challenges and growth targets."
  }];
  return <section data-section-id="12753" id="revenuemanagementfaq" className="bg-background text-foreground py-32 md:py-44">
      <div className="mx-auto max-w-[1440px] px-5 md:px-6 lg:px-12">
        <div ref={ref} className={`grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-6 transition-all duration-[520ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-serif font-bold text-foreground leading-[0.94] tracking-tight" style={{
              fontSize: "clamp(2rem, 5vw, 4rem)"
            }}>
                Revenue{" "}
                <em className="not-italic font-bold text-primary" style={{
                fontStyle: "italic",
                fontStretch: "88%",
                fontVariationSettings: '"wdth" 88'
              }}>
                  FAQ
                </em>
                .
              </h2>

              <p className="mt-8 font-default font-normal text-muted-foreground leading-[1.55]" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Clear answers to common questions about hotel revenue management, 
                OTA strategies, and tech implementation for property owners.
              </p>

              <Link to="/ContactPage" className="group mt-10 inline-flex items-center gap-2 font-default font-medium text-foreground">
                <span className="relative">
                  Ask a question
                  <span className="absolute left-0 right-0 -bottom-1 h-px bg-primary transition-all duration-200 group-hover:h-0.5" />
                </span>
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`faq-${i}`} className="border-b-0 border-t border-border first:border-t last:border-b-0">
                  <AccordionTrigger className="group py-8 hover:no-underline [&>svg]:hidden items-center">
                    <h3 className="font-serif font-semibold text-foreground leading-[1.05] tracking-tight text-left transition-all duration-200 group-hover:[font-variation-settings:'wdth'_108] md:text-[clamp(1.375rem,2.2vw,2rem)]" style={{
                  fontSize: "clamp(1.125rem, 4vw, 1.5rem)"
                }}>
                      {faq.question}
                    </h3>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" className="size-6 shrink-0 text-foreground transition-all duration-[280ms] group-hover:text-primary group-data-[state=open]:rotate-90" style={{
                  transitionTimingFunction: "cubic-bezier(0.2, 0.7, 0.2, 1)"
                }} aria-hidden="true">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-0">
                    <p className="font-default font-normal text-foreground leading-[1.55] max-w-3xl" style={{
                  fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
                }}>
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}